/** Small-venue geodesy. Equirectangular approximations, exact enough indoors. */

const METRES_PER_DEGREE_LAT = 111_320

export interface LngLat {
  lng: number
  lat: number
}

export function metresPerDegreeLng(lat: number): number {
  return METRES_PER_DEGREE_LAT * Math.max(Math.cos((lat * Math.PI) / 180), 1e-6)
}

/**
 * Distance in metres. Equirectangular: at venue scale (hundreds of metres) the
 * error against the haversine formula is well under a centimetre, and this runs
 * once per grid cell per position fix.
 */
export function distanceMeters(a: LngLat, b: LngLat): number {
  const dLat = (b.lat - a.lat) * METRES_PER_DEGREE_LAT
  const dLng = (b.lng - a.lng) * metresPerDegreeLng((a.lat + b.lat) / 2)
  return Math.hypot(dLat, dLng)
}

/** Compass bearing from `a` to `b`, degrees clockwise from north. */
export function bearingDegrees(a: LngLat, b: LngLat): number {
  const dLat = (b.lat - a.lat) * METRES_PER_DEGREE_LAT
  const dLng = (b.lng - a.lng) * metresPerDegreeLng((a.lat + b.lat) / 2)
  return (Math.atan2(dLng, dLat) * 180) / Math.PI
}

const COMPASS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'] as const

export function compassPoint(bearing: number): string {
  const index = Math.round((((bearing % 360) + 360) % 360) / 45) % 8
  return COMPASS[index]
}

export interface Bounds {
  minLng: number
  minLat: number
  maxLng: number
  maxLat: number
}

export function boundsOf(features: GeoJSON.Feature[]): Bounds | null {
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity

  const visit = (coords: unknown): void => {
    if (!Array.isArray(coords)) return
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      const [lng, lat] = coords as [number, number]
      if (lng < minLng) minLng = lng
      if (lat < minLat) minLat = lat
      if (lng > maxLng) maxLng = lng
      if (lat > maxLat) maxLat = lat
      return
    }
    for (const child of coords) visit(child)
  }

  for (const feature of features) {
    if (feature.geometry && 'coordinates' in feature.geometry) {
      visit(feature.geometry.coordinates)
    }
  }

  return Number.isFinite(minLng) ? { minLng, minLat, maxLng, maxLat } : null
}

/** Ray casting against one ring. */
function inRing(point: LngLat, ring: number[][]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    const intersects =
      yi > point.lat !== yj > point.lat &&
      point.lng < ((xj - xi) * (point.lat - yi)) / (yj - yi || Number.EPSILON) + xi
    if (intersects) inside = !inside
  }
  return inside
}

/** True when the point is inside the outer ring and outside every hole. */
function inPolygon(point: LngLat, polygon: number[][][]): boolean {
  if (polygon.length === 0 || !inRing(point, polygon[0])) return false
  for (let i = 1; i < polygon.length; i++) {
    if (inRing(point, polygon[i])) return false
  }
  return true
}

/** True when the point falls inside any of the given (Multi)Polygon features. */
export function insideAny(point: LngLat, features: GeoJSON.Feature[]): boolean {
  for (const feature of features) {
    const geometry = feature.geometry
    if (geometry?.type === 'Polygon') {
      if (inPolygon(point, geometry.coordinates)) return true
    } else if (geometry?.type === 'MultiPolygon') {
      for (const polygon of geometry.coordinates) {
        if (inPolygon(point, polygon)) return true
      }
    }
  }
  return false
}

/** A closed square ring of `sizeMeters`, centred on the point. */
export function squareRing(center: LngLat, sizeMeters: number): number[][] {
  const half = sizeMeters / 2
  const dLat = half / METRES_PER_DEGREE_LAT
  const dLng = half / metresPerDegreeLng(center.lat)
  return [
    [center.lng - dLng, center.lat - dLat],
    [center.lng + dLng, center.lat - dLat],
    [center.lng + dLng, center.lat + dLat],
    [center.lng - dLng, center.lat + dLat],
    [center.lng - dLng, center.lat - dLat],
  ]
}
